import type {ReactElement} from 'react';
import {ControlledListItemType2} from '@Components/controlled-list/components/controlled-list-item-type-2';
import {useAppDispatch, useAppSelector} from '../../../../hooks';
import type {LIST_ITEM_TYPE, ListDefaultItemBaseStorage, ListDefaultSubItemBaseStorage} from '../../list.types';
import {areSubItemsOfCorrespondingListItemBeingShown, updateListItemSelectionState, updateListSubItemSelectedState} from '../../list-reducer';
import type {TextSize} from '@Components/text';
import type {ThumbnailData} from '@Components/thumbnail/thumbnail.types';

interface ListItemType2Props {
  id: string;
  listId: string;
}

export interface ListItemType2Storage extends ListDefaultItemBaseStorage, ListBaseItemType2Storage {
  type: LIST_ITEM_TYPE.DEFAULT_2;
  subItems?: ListSubItemStorageType2[];
}

interface ListBaseItemType2Storage {
  subtext: string;
  thumbnail?: ThumbnailData;
  isDisabled?: boolean;
  hasPulsatingDot?: boolean;
  textClasses?: string;
  textSize?: TextSize;
}

export interface ListSubItemStorageType2 extends ListDefaultSubItemBaseStorage, ListBaseItemType2Storage {
  isSelected?: boolean;
}

export function ListItemType2({...props}: ListItemType2Props) {
  const dispatch = useAppDispatch();
  const listItemData = useAppSelector((state) => {
    return state.list.listItemData.listItemsHashMap[props.id] as ListItemType2Storage;
  });
  const hideListItem = useAppSelector((state) => {
    return areSubItemsOfCorrespondingListItemBeingShown(state.list, props.listId, props.id);
  });

  const onClickForListItem = () => {
    dispatch(
      updateListItemSelectionState({
        listId: props.listId,
        id: props.id,
      })
    );
    if (listItemData.onClick) {
      listItemData.onClick(props.listId, props.id);
    }
  };

  const onClickForListSubItem = (subItemId?: string) => {
    const {subItems} = listItemData;
    if (subItemId && subItems) {
      dispatch(
        updateListSubItemSelectedState({
          listItemId: props.id,
          id: subItemId,
        })
      );

      for (let index = 0; index < subItems.length; index++) {
        const subItem = subItems[index];
        if (subItem.id === subItemId) {
          if (subItem.onClick) {
            subItem.onClick(props.id, subItemId);
          }
        }
      }
    }
  };

  const onClickHandler = (isSubItem = false, subItemId?: string) => {
    if (!isSubItem) {
      onClickForListItem();
    } else {
      onClickForListSubItem(subItemId);
    }
  };

  const display = () => {
    if (!listItemData) {
      return null;
    }
    if (hideListItem) {
      return null;
    }
    if (listItemData.isSelected && listItemData.subItems) {
      return displayListSubItems();
    }
    return displayListItemOfTypeTwo(listItemData);
  };

  const displayListItemOfTypeTwo = (data: ListItemType2Storage | ListSubItemStorageType2, isSubItem = false, subItemId?: string) => {
    return (
      <ControlledListItemType2
        onClick={() => {
          !data.isDisabled ? onClickHandler(isSubItem, subItemId) : null;
        }}
        id={data.id}
        className={data.className ? data.className : ''}
        key={data.id}
        subText={data.subtext}
        text={data.text}
        isDisabled={data.isDisabled}
        thumbnail={data.thumbnail}
        hasPulsatingDot={data.hasPulsatingDot}
        textClasses={data.textClasses}
        textSize={data.textSize}
      />
    );
  };

  const displayListSubItems = (): ReactElement<any>[] => {
    const listSubItems: ReactElement<any>[] = [];
    if (!listItemData.subItems) {
      return listSubItems;
    }

    listItemData.subItems.forEach((subItem) => {
      listSubItems.push(displayListItemOfTypeTwo(subItem, true, subItem.id));
    });

    return listSubItems;
  };

  return <>{display()}</>;
}
